import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["isRanking", "testId"],
  _excluded2 = ["width", "children", "isSortable", "sortOrder", "isFixedSize", "shouldTruncate", "onClick", "style", "testId"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* eslint-disable @repo/internal/react/require-jsdoc */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { B100, N30A } from '@atlaskit/theme/colors';
import { ASC, DESC } from '../internal/constants';
import { arrow, head, MSThemeColors, tableBorder } from '../theme';
import { cellStyles, fixedSizeTruncateStyles, getTruncationStyleVars, overflowTruncateStyles, truncationWidthStyles } from './constants';
var CSS_VAR_TEXT_COLOR = '--local-dynamic-table-text-color';
var rankingStyles = css({
  display: 'block'
});
var headStyles = css({
  borderBlockEnd: "none"
});
export var Head = function Head(_ref) {
  var isRanking = _ref.isRanking,
    testId = _ref.testId,
    props = _objectWithoutProperties(_ref, _excluded);
  return jsx("thead", _extends({
    css: [headStyles, isRanking && rankingStyles],
    "data-testid": testId
    // eslint-disable-next-line @repo/internal/react/no-unsafe-spread-props
  }, props));
};
var headCellBaseStyles = css({
  boxSizing: 'border-box',
  position: 'relative',
  border: 'none',
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
  borderBlockEnd: "2px solid ".concat(tableBorder.borderColor),
  color: "var(--ds-text-subtle, ".concat("var(".concat(CSS_VAR_TEXT_COLOR, ")"), ")"),
  font: "var(--ds-font-body-UNSAFE_small, normal 400 12px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, system-ui, \"Helvetica Neue\", sans-serif)",
  fontWeight: "var(--ds-font-weight-bold, 700)",
  textAlign: 'left',
  verticalAlign: 'top',
  '&:focus-visible': {
    outline: "solid 2px ".concat("var(--ds-border-focused, ".concat(B100, ")"))
  }
});
var onClickStyles = css({
  '&:hover': {
    backgroundColor: "var(--ds-background-neutral-hovered, ".concat(N30A, ")"),
    cursor: 'pointer'
  }
});
var baseStyles = css({
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '& > button': {
    padding: "var(--ds-space-0, 0)",
    position: 'relative',
    appearance: 'none',
    background: 'none',
    border: 'none',
    color: 'inherit',
    cursor: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    '&::before, &::after': {
      display: 'block',
      width: 0,
      height: 0,
      position: 'absolute',
      border: '3px solid transparent',
      content: '""',
      insetInlineEnd: "var(--ds-space-negative-100, -8px)"
    },
    '&::before': {
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
      borderBlockEnd: "3px solid ".concat(arrow.defaultColor),
      insetBlockEnd: "var(--ds-space-100, 8px)"
    },
    '&::after': {
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
      borderBlockStart: "3px solid ".concat(arrow.defaultColor),
      insetBlockEnd: 0
    }
  },
  '@media (forced-colors: active)': {
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
    '& > button': {
      '&::before, &::after': {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
        border: "3px solid ".concat(MSThemeColors.Background)
      },
      '&::before': {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
        borderBlockEnd: "3px solid ".concat(MSThemeColors.Text)
      },
      '&::after': {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
        borderBlockStart: "3px solid ".concat(MSThemeColors.Text)
      }
    }
  }
});
var ascendingStyles = css({
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '& > button': {
    '&::before': {
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
      borderBlockEnd: "3px solid ".concat(arrow.selectedColor)
    }
  },
  '@media (forced-colors: active)': {
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
    '& > button': {
      '&::before': {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
        borderBlockEnd: "3px solid ".concat(MSThemeColors.SelectedBackground)
      }
    }
  }
});
var descendingStyles = css({
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '& > button': {
    '&::after': {
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
      borderBlockStart: "3px solid ".concat(arrow.selectedColor)
    }
  },
  '@media (forced-colors: active)': {
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
    '& > button': {
      '&::after': {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
        borderBlockStart: "3px solid ".concat(MSThemeColors.SelectedBackground)
      }
    }
  }
});
export var HeadCell = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var width = _ref2.width,
    children = _ref2.children,
    isSortable = _ref2.isSortable,
    sortOrder = _ref2.sortOrder,
    isFixedSize = _ref2.isFixedSize,
    shouldTruncate = _ref2.shouldTruncate,
    onClick = _ref2.onClick,
    style = _ref2.style,
    testId = _ref2.testId,
    rest = _objectWithoutProperties(_ref2, _excluded2);
  var mergedStyles = _objectSpread(_objectSpread(_objectSpread({}, style), width && getTruncationStyleVars({
    width: width
  })), {}, _defineProperty({}, CSS_VAR_TEXT_COLOR, head.textColor));
  var isASC = sortOrder === ASC;
  var isDESC = sortOrder === DESC;
  var getFormattedSortOrder = function getFormattedSortOrder() {
    if (isASC) {
      return 'ascending';
    } else if (isDESC) {
      return 'descending';
    }
  };

  // If there is no content in the cell, it should be rendered as an empty `td`, not a `th`.
  // https://dequeuniversity.com/rules/axe/4.7/empty-table-header
  var Component = children ? 'th' : 'td';
  return jsx(Component, _extends({
    "aria-sort": getFormattedSortOrder()
    // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
    ,
    style: mergedStyles,
    css: [
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
    cellStyles, headCellBaseStyles, onClick && onClickStyles,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
    truncationWidthStyles,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
    isFixedSize && shouldTruncate && fixedSizeTruncateStyles,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
    isFixedSize && overflowTruncateStyles, isSortable && baseStyles, isASC && ascendingStyles, isDESC && descendingStyles],
    onClick: onClick,
    ref: ref,
    "data-testid": testId
  }, rest), children);
});